import { AuditoriaUsuariosService } from '@app/services/auditoria-usuarios.service';
import { IngresosCrmService } from '@app/services/ingresos-crm.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { environment } from '@src/environments/environment';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, tap, map, filter, take } from 'rxjs/operators';
import * as fromActions from './user.actions';
import { UserResponse } from './user.models';
import { UserPageResponse } from './user.models';
import { GeneralService } from '@app/services/general.service';
import { Store } from '@ngrx/store';
import { GenericResponse, UserResponse2 } from '@app/models/backend';
// Eliminamos la importación no utilizada
import { UserStatusLoggerService } from '@app/services/user-status-logger.service';
import { WebSocketService } from '@app/services/websocket/WebSocketService';
import { NotificationService } from '@app/services/notification/notification.service';
import { AuthInterceptor } from '@app/interceptors/auth-interceptor';

// Función de utilidad para asegurar que la URL base termine con una barra diagonal
function getBaseUrl(): string {
  return environment.url.endsWith('/')
    ? environment.url
    : environment.url + '/';
}

type Action = fromActions.All;

@Injectable()
export class UserEffects {
  constructor(
    private actions: Actions,
    private router: Router,
    private httpClient: HttpClient,
    public GeneralService: GeneralService,
    private store: Store,
    private userStatusLogger: UserStatusLoggerService,
    private webSocketService: WebSocketService,
    private notification: NotificationService,
    private authInterceptor: AuthInterceptor,
    private auditoriaUsuarios: AuditoriaUsuariosService,
    private ingresosCrm: IngresosCrmService
  ) { }

  /**
   * Renueva el token después de actualizar un usuario
   */
  private refreshTokenAfterUpdate(): void {
    const refreshTokenUrl = `${environment.url}api/authentication/refresh-token-after-update`;

    this.httpClient
      .post<GenericResponse<any>>(refreshTokenUrl, null)
      .pipe(
        tap((response) => {
          if (response.rpta === 1 && response.data?.token) {
            // Actualizar el token en localStorage
            localStorage.setItem('token', response.data.token);

            // Actualizar la información del usuario en localStorage
            const currentUser = JSON.parse(
              localStorage.getItem('user') || '{}'
            );
            const updatedUserData = {
              ...currentUser,
              userId: response.data.userId,
              username: response.data.username,
              nombre: response.data.nombre,
              apellido: response.data.apellido,
              email: response.data.email,
              role: response.data.role,
              sede: response.data.sede,
              sedeId: response.data.sedeId,
            };
            localStorage.setItem('user', JSON.stringify(updatedUserData));
            this.GeneralService.usuario$ = updatedUserData;

            this.notification.success('Sesión actualizada correctamente');
          } else {
            console.error(
              'Error al renovar token después de actualización:',
              response.msg
            );
          }
        }),
        catchError((error) => {
          console.error(
            'Error al renovar token después de actualización:',
            error
          );
          // Si falla la renovación, mostrar mensaje pero no cerrar sesión
          this.notification.warning(
            'Por favor, actualiza la página para reflejar los cambios'
          );
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * Maneja la expiración del token JWT
   * @param err Error HTTP
   * @returns true si el token expiró y se manejó, false en caso contrario
   */
  private handleTokenExpiration(err: any): boolean {
    // ======================= INICIO DE LA MODIFICACIÓN =======================
    // Se elimina "err.status === 403" de la condición.
    // Esta función ahora solo debe manejar errores de autenticación (401), no de autorización (403).
    if (
      err.error?.error === 'TOKEN_EXPIRADO' ||
      err.error?.message?.includes('JWT expired') ||
      err.status === 401
    ) {
      // ======================== FIN DE LA MODIFICACIÓN =========================

      // Preservar información importante antes de limpiar localStorage
      let preservedData = {};
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          if (user.sede_id) {
            preservedData = { sede_id: user.sede_id, sede: user.sede };
          }
        }
      } catch (error) {
        console.error('Error al preservar datos de usuario:', error);
      }

      localStorage.clear();
      sessionStorage.clear();
      //this.notification.error('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');

      if (Object.keys(preservedData).length > 0) {
        sessionStorage.setItem(
          'preservedUserData',
          JSON.stringify(preservedData)
        );
      }

      this.router.navigate(['/auth/login']);

      if (this.webSocketService.isConnected()) {
        this.webSocketService.disconnect();
      }
      return true;
    }
    return false;
  }

  // Efecto para actualizar usuario
  updateUser$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(fromActions.Types.UPDATE_USER),
      switchMap((action: fromActions.UpdateUser) => {
        const token = localStorage.getItem('token');
        if (!token) {
          this.router.navigate(['/auth/login']);
          return of(new fromActions.UpdateUserError('No hay sesión activa'));
        }

        // Preparar los datos para el backend
        const userData: any = { ...action.user };

        // Si tenemos sede_id, usarlo directamente sin crear un objeto sede
        if (userData.sede_id) {
          // Mantener sede_id como está, no crear un objeto sede
          delete userData.sedeId; // Eliminar campo redundante
        } else if (userData.sedeId) {
          // Si no hay sede_id pero hay sedeId, usar ese
          userData.sede_id = userData.sedeId;
          delete userData.sedeId;
        }

        // Eliminar el campo sede si existe como objeto para evitar conflictos
        if (userData.sede && typeof userData.sede === 'object') {
          delete userData.sede;
        }

        // Eliminar el campo sedeNombre si existe
        if (userData.sedeNombre) {
          delete userData.sedeNombre;
        }

        // Ozaru registra responsable y valores previos antes de enviar el cambio a Java.
        // También resuelve allí la compatibilidad de BACKOFFICE 6 MESES.
        return this.auditoriaUsuarios.actualizar(action.id, userData).pipe(
          tap((response) => {
            if (response.rpta === 1) {
              this.notification.success(response.msg || 'Usuario actualizado exitosamente');
              if (response.data?.tokenRefreshRequired) this.refreshTokenAfterUpdate();
            } else {
              this.notification.error(response.msg || 'Error al actualizar usuario');
            }
          }),
          map((response) => response.rpta === 1
            ? new fromActions.UpdateUserSuccess(response.data)
            : new fromActions.UpdateUserError(response.msg)),
          catchError((err) => {
            const mensaje = this.mensajeErrorAuditoria(err);
            this.notification.error(mensaje);
            return of(new fromActions.UpdateUserError(mensaje));
          })
        );
      })
    )
  );

  private mensajeErrorAuditoria(err: any): string {
    if (typeof err.error?.detail === 'string') return err.error.detail;
    if (err.status === 401) return 'No se pudo validar la sesión. Inicia sesión nuevamente antes de guardar.';
    return 'No se pudo confirmar el cambio. Revisa el historial del usuario antes de volver a intentarlo.';
  }

  // 🔥 NUEVO: Efecto para actualizar perfil del usuario actual
  updateProfile$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(fromActions.Types.UPDATE_PROFILE),
      switchMap((action: fromActions.UpdateProfile) => {
        const token = localStorage.getItem('token');
        if (!token) {
          this.router.navigate(['/auth/login']);
          return of(new fromActions.UpdateProfileError('No hay sesión activa'));
        }

        return this.httpClient
          .put<GenericResponse<UserResponse>>(
            `${environment.url}api/user/perfil`,
            action.profileData
          )
          .pipe(
            tap((response) => {
              if (response.rpta === 1) {
                this.notification.success(
                  response.msg || 'Perfil actualizado exitosamente'
                );

                // 🔥 ACTUALIZAR LOCALSTORAGE EN TIEMPO REAL
                const currentUser = JSON.parse(
                  localStorage.getItem('user') || '{}'
                );
                const updatedUser = { ...currentUser, ...response.data };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                console.log(
                  '🔥 Usuario actualizado en localStorage:',
                  updatedUser
                );

                // Actualizar el servicio general
                this.GeneralService.usuario$ = updatedUser;
              } else {
                this.notification.error(
                  response.msg || 'Error al actualizar perfil'
                );
              }
            }),
            map((response) => {
              if (response.rpta === 1) {
                return new fromActions.UpdateProfileSuccess(response.data);
              } else {
                return new fromActions.UpdateProfileError(response.msg);
              }
            }),
            catchError((err) => {
              this.notification.error('Error al actualizar perfil');
              return of(new fromActions.UpdateProfileError(err.message));
            })
          );
      })
    )
  );

  // Efecto para eliminar usuario (soft delete)
  deleteUser$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(fromActions.Types.DELETE_USER),
      switchMap((action: fromActions.DeleteUser) => {
        const token = localStorage.getItem('token');
        if (!token) {
          this.router.navigate(['/auth/login']);
          return of(new fromActions.DeleteUserError('No hay sesión activa'));
        }

        return this.auditoriaUsuarios.desactivar(action.id).pipe(
          tap((response) => {
            if (response.rpta === 1) {
              this.notification.success(
                response.msg || 'Usuario eliminado exitosamente'
              );
            } else {
              this.notification.error(
                response.msg || 'Error al eliminar usuario'
              );
            }
          }),
          map((response) => {
            if (response.rpta === 1) {
              return new fromActions.DeleteUserSuccess(action.id);
            } else {
              return new fromActions.DeleteUserError(response.msg);
            }
          }),
          catchError((err) => {
            const mensaje = this.mensajeErrorAuditoria(err);
            this.notification.error(mensaje);
            return of(new fromActions.DeleteUserError(mensaje));
          })
        );
      })
    )
  );

  loadUsersPage: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(fromActions.Types.LOAD_USERS_PAGE),
      switchMap((action: fromActions.LoadUsersPage) => {
        let url = `${environment.url}api/user/listar?page=${action.page}&size=${action.size}`;
        // ✅ LÓGICA DE `soloConectados` ELIMINADA
        if (action.estado) {
          url += `&estado=${action.estado}`;
        }

        return this.httpClient.get<GenericResponse<UserPageResponse>>(url).pipe(
          map((response) =>
            response.rpta === 1
              ? new fromActions.LoadUsersPageSuccess(response.data)
              : new fromActions.LoadUsersPageError(response.msg)
          ),
          catchError((err) => {
            this.notification.error('Error al cargar usuarios');
            return of(new fromActions.LoadUsersPageError(err.message));
          })
        );
      })
    )
  );

  searchUsers$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(fromActions.Types.SEARCH_USERS),
      switchMap((action: fromActions.SearchUsers) => {
        let url = `${environment.url}api/user/buscar?page=${action.page}&size=${action.size}`;
        if (action.query && action.query.trim() !== '') {
          url += `&query=${encodeURIComponent(action.query)}`;
        }
        if (action.sedeId) {
          url += `&sede_id=${action.sedeId}`;
        }
        // ✅ LÓGICA DE `soloConectados` ELIMINADA
        if (action.estado) {
          url += `&estado=${action.estado}`;
        }
        if (action.rol) {
          url += `&rol=${action.rol}`;
        }
        if (action.tipoTrabajo) {
          url += `&tipoTrabajo=${action.tipoTrabajo}`;
        }
        if (action.fechaCese) {
          url += `&fechaCese=${action.fechaCese}`;
        }

        return this.httpClient.get<GenericResponse<UserPageResponse>>(url).pipe(
          map((response) =>
            response.rpta === 1
              ? new fromActions.SearchUsersSuccess(response.data)
              : new fromActions.SearchUsersError(response.msg)
          ),
          catchError((err) => {
            this.notification.error('Error al buscar usuarios');
            return of(new fromActions.SearchUsersError(err.message));
          })
        );
      })
    )
  );

  /** SIGN-IN ─────────────────────────────────────────────────────────── */
  signInEmail$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(fromActions.Types.SIGIN_IN_EMAIL),
      map((a: fromActions.SignInEmail) => a.credentials),

      switchMap((credentials) =>
        this.httpClient
          .post<GenericResponse<any>>(
            `${getBaseUrl()}api/authentication/sign-in`,
            credentials
          )
          .pipe(
            this.ingresosCrm.observarLogin(),
            /* ─── efectos secundarios (guardar datos, WS…) ─── */
            tap((resp) => {
              if (resp.rpta !== 1) {
                // No mostrar notification aquí, se manejará en el componente
                return;
              }

              const d = resp.data;
              const user = {
                id: d.userId,
                username: d.username,
                nombre: d.nombre,
                apellido: d.apellido,
                role: d.role,
                token: d.token,
                sede: d.sede,
                sede_id: d.sede_id,
                picture: d.picture,
                email: d.email,
                tipoTrabajo: d.tipoTrabajo,
              };

              /* restaurar nombre de sede si se preservó tras logout */
              try {
                const prev = JSON.parse(
                  sessionStorage.getItem('preservedUserData') ?? '{}'
                );
                if (prev.sede_id === user.sede_id) user.sede = prev.sede;
                sessionStorage.removeItem('preservedUserData');
              } catch { }

              /* persistir en storage */
              localStorage.setItem('token', d.token);
              localStorage.setItem('user', JSON.stringify(user));
              d.coordinador
                ? localStorage.setItem(
                  'coordinador',
                  JSON.stringify(d.coordinador)
                )
                : localStorage.removeItem('coordinador');
              d.asesores
                ? localStorage.setItem('asesores', JSON.stringify(d.asesores))
                : localStorage.removeItem('asesores');
              localStorage.setItem('userStatus', d.status || 'OFFLINE');

              /* exponer al servicio global */
              this.GeneralService.usuario$ = user;

              /* 🔧 CORREGIDO: Resetear estado del interceptor después de login exitoso */
              this.authInterceptor.resetInterceptorState();

              /* WebSocket + logger  (solo en login) */
              this.webSocketService.connect();
              this.userStatusLogger.initialize();

              /* Conectar usuario automáticamente al hacer login */
              if (user.id) {
                console.log(
                  '🔗 Conectando usuario automáticamente en login:',
                  user.id
                );
                // Usar setTimeout para asegurar que el token esté guardado en localStorage
                setTimeout(() => {
                  // Hacer petición HTTP directa para conectar al usuario
                  const connectUrl = `${environment.url}api/user/connect/${user.id}`;
                  const headers = { Authorization: `Bearer ${d.token}` };

                  this.httpClient
                    .post<any>(connectUrl, {}, { headers })
                    .subscribe({
                      next: (response) => {
                        // Actualizar localStorage para que el componente lo detecte
                        if (response && response.rpta === 1 && response.data) {
                          localStorage.setItem(
                            'userStatus',
                            response.data.status
                          );
                          // Forzar evento de storage para que los componentes se actualicen
                          window.dispatchEvent(
                            new StorageEvent('storage', {
                              key: 'userStatus',
                              newValue: response.data.status,
                              oldValue: localStorage.getItem('userStatus'),
                            })
                          );
                        }
                      },
                      error: (error) => {
                        console.error(
                          '❌ Error al conectar usuario en login:',
                          error
                        );
                      },
                    });
                }, 100);
              }

              (
                this.webSocketService.getConnectionStatus() as Observable<boolean>
              )
                .pipe(
                  filter((c: boolean) => c),
                  take(1)
                )
                .subscribe(() => {
                  /* servicios que dependen del socket */
                  // TODO: Reimplementar servicios WebSocket si es necesario
                  // Los servicios AnuncioWsService y SedeUserService fueron eliminados
                  // en la simplificación del WebSocket
                });

              /* ⚠️ Sin navegación: LoginComponent decide la ruta
               cuando su overlay termina. */
            }),

            /* ─── acciones de éxito / error ─── */
            map((resp) =>
              resp.rpta === 1
                ? new fromActions.SignInEmailSuccess(
                  resp.data.username,
                  {
                    id: resp.data.userId,
                    username: resp.data.username,
                    nombre: resp.data.nombre,
                    apellido: resp.data.apellido,
                    role: resp.data.role,
                    token: resp.data.token,
                    sede: resp.data.sede,
                    sede_id: resp.data.sede_id,
                    picture: resp.data.picture,
                    email: resp.data.email,
                    tipoTrabajo: resp.data.tipoTrabajo,
                  },
                  resp.data.status || 'OFFLINE'
                )
                : new fromActions.SignInEmailError(resp.msg)
            ),

            /* ─── manejo de errores HTTP ─── */
            catchError((err) => {
              // Para errores de login (401), obtener el mensaje específico del backend
              let msg = 'Error al iniciar sesión';

              if (err.status === 0) {
                msg = 'No se pudo conectar con el servidor.';
              } else if (err.status === 401 && err.error?.msg) {
                // Error de credenciales incorrectas del backend
                msg = err.error.msg;
              } else if (err.error?.msg) {
                msg = err.error.msg;
              } else if (err.message) {
                msg = err.message;
              }

              // No mostrar notification aquí, se manejará en el componente
              return of(new fromActions.SignInEmailError(msg));
            })
          )
      )
    )
  );

  // -----------------------------------------------------------------------------
  //  E F E C T O   ·  R E G I S T E R   U S E R
  // -----------------------------------------------------------------------------
  registerUser$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromActions.Types.REGISTER_USER),
      switchMap((action: fromActions.RegisterUser) => {
        /* ----------------------------------------------------------------------
         1.  Verificar sesión
      ---------------------------------------------------------------------- */
        const token = localStorage.getItem('token');
        if (!token) {
          this.router.navigate(['/auth/login']);
          return of(new fromActions.RegisterUserError('No hay sesión activa'));
        }

        /* ----------------------------------------------------------------------
         2.  Normalizar payload
      ---------------------------------------------------------------------- */
        const userData: any = { ...action.user };

        // ─── Sede ────────────────────────────────────────────────────────────
        // Si llega { sede:{ id: 2 } } ==> enviamos sede_id: 2
        if (userData.sede && userData.sede.id) {
          userData.sede_id = userData.sede.id;
        }
        delete userData.sede; // el backend NO espera el objeto sede

        // ─── Teléfono ────────────────────────────────────────────────────────
        userData.telefono = userData.telefono
          ? String(userData.telefono)
          : null;

        // ─── Email ───────────────────────────────────────────────────────────
        if (!userData.email || userData.email === '') {
          userData.email = null; // el backend generará username@midas.pe
        }

        // ─── Estado, DNI (asegurar formatos) ─────────────────────────────────
        userData.estado = 'A';
        if (userData.dni) userData.dni = String(userData.dni);

        console.log('🛰️  Enviando a /api/user/registrar 👉', userData);

        /* ----------------------------------------------------------------------
         3.  POST al backend
      ---------------------------------------------------------------------- */
        return this.httpClient
          .post<GenericResponse<UserResponse>>(
            `${getBaseUrl()}api/user/registrar`,
            userData
          )
          .pipe(
            /* -------------------- Éxito -------------------- */
            tap((resp) => {
              if (resp.rpta === 1) {
                // ✅ NO mostrar notificación aquí, se maneja en el componente
                this.store.dispatch(new fromActions.Init()); // refrescar lista
              }
              // ✅ NO mostrar notificación de error aquí, se maneja en el componente
            }),
            map((resp) =>
              resp.rpta === 1
                ? new fromActions.RegisterUserSuccess(resp.data)
                : new fromActions.RegisterUserError(resp.msg)
            ),
            /* -------------------- Error -------------------- */
            catchError((err) => {
              let msg = 'Error al registrar el usuario.';
              if (err.error?.msg) msg = err.error.msg;
              else if (typeof err.error === 'string') {
                try {
                  msg = JSON.parse(err.error).msg || msg;
                } catch { }
              } else if (err.message) msg = err.message;

              // ✅ NO mostrar notificación aquí, se maneja en el componente
              return of(new fromActions.RegisterUserError(msg));
            })
          );
      })
    )
  );

  // Efecto para inicializar usuario (Init)
  init: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(fromActions.Types.INIT),
      switchMap(() => {
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('🔍 No hay token en localStorage, usuario no autorizado');
          return of(new fromActions.InitUnauthorized());
        }

        return this.httpClient
          .get<GenericResponse<UserResponse2>>(`${getBaseUrl()}api/user`)
          .pipe(
            tap((response) => {
              if (response.rpta === 1) {
                // 👉 Guardar nuevo token si viene en la respuesta
                if (response.data.user.token) {
                  localStorage.setItem('token', response.data.user.token);
                  console.log('🔁 Token renovado y guardado en localStorage');
                }

                // Normalizar la estructura del objeto user para que coincida con la estructura del login
                const normalizedUser: any = {
                  id: response.data.user.id,
                  username: response.data.user.username,
                  nombre: response.data.user.nombre,
                  apellido: response.data.user.apellido,
                  role: response.data.user.role,
                  picture: response.data.user.picture,
                  tipoTrabajo: response.data.user.tipoTrabajo,
                  token:
                    response.data.user.token ||
                    localStorage.getItem('token') ||
                    '',
                  email: response.data.user.email,
                  // Manejar la sede correctamente
                  sede_id: response.data.user.sede_id,
                  // Usar sedeNombre si existe, o sede si es un string
                  sede:
                    response.data.user.sedeNombre ||
                    (response.data.user.sede &&
                      typeof response.data.user.sede === 'string'
                      ? response.data.user.sede
                      : null),
                };
                // Preservar el nombre de la sede si ya existe en localStorage
                try {
                  const existingUserStr = localStorage.getItem('user');
                  if (existingUserStr) {
                    const existingUser = JSON.parse(existingUserStr);
                    // Si el usuario existente tiene un nombre de sede pero la respuesta no lo tiene,
                    // preservar el nombre existente
                    if (
                      existingUser.sede &&
                      typeof existingUser.sede === 'string' &&
                      !normalizedUser.sede
                    ) {
                      normalizedUser.sede = existingUser.sede;
                    }

                    // Si el usuario existente tiene un sede_id pero la respuesta no lo tiene,
                    // preservar el sede_id existente
                    if (existingUser.sede_id && !normalizedUser.sede_id) {
                      normalizedUser.sede_id = existingUser.sede_id;
                    }
                  }
                } catch (error) {
                  console.error(
                    'Error al preservar el nombre de la sede:',
                    error
                  );
                }
                // Si tenemos un ID de sede pero no un nombre, usar el nombre de la sede del backend o "tu sede" como valor predeterminado
                if (normalizedUser.sede_id && !normalizedUser.sede) {
                  normalizedUser.sede =
                    response.data.user.sedeNombre || 'tu sede';
                  console.log(
                    '🔁 Usando nombre de sede predeterminado:',
                    normalizedUser.sede
                  );
                }

                // Guardar el objeto user normalizado en localStorage
                localStorage.setItem('user', JSON.stringify(normalizedUser));

                // 🔥 NUEVO: Conectar usuario automáticamente en init (F5) ANTES de guardar el estado del backend
                if (normalizedUser.id) {
                  // Hacer petición HTTP para conectar al usuario
                  const connectUrl = `${environment.url}api/user/connect/${normalizedUser.id}`;
                  const token = localStorage.getItem('token');
                  const options = token
                    ? { headers: { Authorization: `Bearer ${token}` } }
                    : {};

                  this.httpClient.post<any>(connectUrl, {}, options).subscribe({
                    next: (connectResponse) => {
                      // Actualizar localStorage con el estado de conexión REAL
                      if (
                        connectResponse &&
                        connectResponse.rpta === 1 &&
                        connectResponse.data
                      ) {
                        localStorage.setItem(
                          'userStatus',
                          connectResponse.data.status
                        );
                      }
                    },
                    error: (error) => {
                      console.error(
                        '❌ Error al conectar usuario en init:',
                        error
                      );
                      // En caso de error, usar el estado del backend como fallback
                      const backendStatus = response.data.status || 'OFFLINE';
                      localStorage.setItem('userStatus', backendStatus);
                      console.log(
                        '💾 Estado de fallback guardado:',
                        backendStatus
                      );
                    },
                  });
                } else {
                  // Si no hay ID de usuario, usar el estado del backend
                  const backendStatus = response.data.status || 'OFFLINE';
                  localStorage.setItem('userStatus', backendStatus);
                  console.log(
                    '💾 Estado del backend guardado (sin ID):',
                    backendStatus
                  );
                }

                if (response.data.coordinador) {
                  localStorage.setItem(
                    'coordinador',
                    JSON.stringify(response.data.coordinador)
                  );
                } else {
                  localStorage.removeItem('coordinador');
                }

                // Actualizar el servicio general con el usuario normalizado
                this.GeneralService.usuario$ = normalizedUser;
              }
            }),

            map((response) => {
              if (response.rpta === 1) {
                // No conectamos el WebSocket aquí, solo en el login exitoso
                // Obtener el usuario normalizado de localStorage para asegurar consistencia
                try {
                  const userStr = localStorage.getItem('user');
                  if (userStr) {
                    const user = JSON.parse(userStr);
                    return new fromActions.InitAuthorized(user.username, user);
                  }
                } catch (error) {
                  console.error(
                    'Error al obtener usuario de localStorage:',
                    error
                  );
                }

                // Si hay algún error, usar el objeto original como respaldo
                return new fromActions.InitAuthorized(
                  response.data.user.username,
                  response.data.user
                );
              } else {
                return new fromActions.InitUnauthorized();
              }
            }),
            catchError((err) => {
              console.error('Error en la inicialización:', err);

              // Si es error 403 o 401, limpiar sesión inmediatamente
              if (err.status === 403 || err.status === 401) {
                localStorage.clear();
                sessionStorage.clear();
                this.router.navigate(['/auth/login']);
                //this.notification.error('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
                return of(new fromActions.InitUnauthorized());
              }

              // Para otros errores, intentar una vez recargar la página
              const refreshed = sessionStorage.getItem('refreshed');
              if (!refreshed) {
                localStorage.clear();
                sessionStorage.setItem('refreshed', 'true');
                this.router.navigate(['/auth/login']);
                this.notification.error(
                  'Error de conexión. Por favor, inicie sesión nuevamente.'
                );
              } else {
                sessionStorage.removeItem('refreshed');
              }

              return of(new fromActions.InitUnauthorized());
            })
          );
      })
    )
  );

  // Efecto para carga masiva de ASESOR
  cargaMasivaAsesor$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(fromActions.Types.CARGA_MASIVA_ASESOR),
      switchMap((action: fromActions.CargaMasivaAsesor) => {
        const token = localStorage.getItem('token');
        if (!token) {
          this.router.navigate(['/auth/login']);
          return of(
            new fromActions.CargaMasivaAsesorError('No hay sesión activa')
          );
        }

        const formData = new FormData();
        formData.append('file', action.file);

        // Mostrar mensaje de carga
        this.notification.success('Procesando archivo, por favor espere...');

        return this.httpClient
          .post<GenericResponse<string>>(
            `${getBaseUrl()}api/user/crear-masivo`,
            formData
          )
          .pipe(
            tap((response) => {
              if (response.rpta === 1) {
                this.notification.success(
                  response.msg || 'Usuarios ASESOR creados exitosamente'
                );
                // Actualizar la lista de usuarios después de una carga exitosa
                this.store.dispatch(new fromActions.Init());
              } else {
                this.notification.error(
                  response.msg || 'Error al subir archivo para usuarios ASESOR'
                );
              }
            }),
            map((response) => {
              if (response.rpta === 1) {
                return new fromActions.CargaMasivaAsesorSuccess(response.data);
              } else {
                return new fromActions.CargaMasivaAsesorError(response.msg);
              }
            }),
            catchError((err) => {
              // Ya no llamamos a handleTokenExpiration.
              // El interceptor se encargará de los errores 401.
              // Solo mostramos una notificación para otros tipos de errores.
              // Intentar extraer un mensaje de error más específico
              let errorMessage = 'Error al subir archivo para usuarios ASESOR';

              if (err.error && err.error.msg) {
                errorMessage = err.error.msg;
              } else if (err.error && typeof err.error === 'string') {
                try {
                  const errorObj = JSON.parse(err.error);
                  if (errorObj.msg) {
                    errorMessage = errorObj.msg;
                  }
                } catch (e) {
                  // Si no se puede parsear, usar el error como está
                  if (err.error) {
                    errorMessage = err.error;
                  }
                }
              } else if (err.message) {
                errorMessage = err.message;
              }

              // Mostrar mensaje de error
              console.error('Error al cargar usuarios ASESOR:', err);
              this.notification.error(errorMessage);

              return of(new fromActions.CargaMasivaAsesorError(errorMessage));
            })
          );
      })
    )
  );

  // Efecto para carga masiva de BACKOFFICE
  cargaMasivaBackoffice$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(fromActions.Types.CARGA_MASIVA_BACKOFFICE),
      switchMap((action: fromActions.CargaMasivaBackOffice) => {
        const token = localStorage.getItem('token');
        if (!token) {
          this.router.navigate(['/auth/login']);
          return of(
            new fromActions.CargaMasivaBackOfficeError('No hay sesión activa')
          );
        }

        const formData = new FormData();
        formData.append('file', action.file);

        // Mostrar mensaje de carga
        this.notification.success('Procesando archivo, por favor espere...');

        return this.httpClient
          .post<GenericResponse<string>>(
            `${getBaseUrl()}api/user/crear-masivo-backoffice`,
            formData
          )
          .pipe(
            tap((response) => {
              if (response.rpta === 1) {
                this.notification.success(
                  response.msg || 'Usuarios BACKOFFICE creados exitosamente'
                );
                // Actualizar la lista de usuarios después de una carga exitosa
                this.store.dispatch(new fromActions.Init());
              } else {
                this.notification.error(
                  response.msg ||
                  'Error al subir archivo para usuarios BACKOFFICE'
                );
              }
            }),
            map((response) => {
              if (response.rpta === 1) {
                return new fromActions.CargaMasivaBackOfficeSuccess(
                  response.data
                );
              } else {
                return new fromActions.CargaMasivaBackOfficeError(response.msg);
              }
            }),
            catchError((err) => {
              let errorMessage =
                'Error al subir archivo para usuarios BACKOFFICE';

              if (err.error && err.error.msg) {
                errorMessage = err.error.msg;
              } else if (err.error && typeof err.error === 'string') {
                try {
                  const errorObj = JSON.parse(err.error);
                  if (errorObj.msg) {
                    errorMessage = errorObj.msg;
                  }
                } catch (e) {
                  // Si no se puede parsear, usar el error como está
                  if (err.error) {
                    errorMessage = err.error;
                  }
                }
              } else if (err.message) {
                errorMessage = err.message;
              }

              // Mostrar mensaje de error
              console.error('Error al cargar usuarios BACKOFFICE:', err);
              this.notification.error(errorMessage);

              return of(
                new fromActions.CargaMasivaBackOfficeError(errorMessage)
              );
            })
          );
      })
    )
  );
}